import { useParams, Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles, testimonials } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center h-screen pt-16">
          <p className="text-foreground-500 text-lg">文章不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const randomTestimonial = testimonials[Math.floor(Math.random() * testimonials.length)];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "datePublished": article.publishDate,
    "image": article.coverImage,
    "publisher": {
      "@type": "Organization",
      "name": "吃瓜网",
      "url": "http://claekqe.cn/"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `http://claekqe.cn/article/${article.id}`
    },
    "keywords": article.tags.join(", ")
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>

      <Header />
      <main>
        <section className="relative w-full h-[70vh] min-h-[500px] flex items-end overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`吃瓜网 - ${article.title}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/75"></div>

          <div className="absolute top-24 left-0 right-0 z-10 px-4 md:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 pb-10 md:pb-16">
            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6">
              <div className="lg:w-3/5">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-medium">
                    {article.category}
                  </span>
                  {article.featured && (
                    <span className="px-3 py-1 rounded-full bg-accent-500 text-white text-xs font-medium">
                      热门
                    </span>
                  )}
                </div>
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight">
                  {article.title}
                </h1>
              </div>
              <div className="lg:w-2/5 lg:text-right">
                <div className="flex items-center lg:justify-end gap-3 text-white/60 text-sm mb-2">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span>{article.publishDate}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                  </span>
                </div>
                <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-md lg:ml-auto">
                  {article.excerpt}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <article className="article-content">
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            </article>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
              <div className="md:w-3/5">
                <span className="text-foreground-400 text-xs font-medium tracking-wider mb-2 inline-block">
                  &bull; 网友热评
                </span>
                <blockquote className="font-heading text-2xl md:text-4xl text-foreground-950 leading-relaxed">
                  <span className="text-foreground-300 text-5xl md:text-7xl">&ldquo;</span>
                  <br />
                  {randomTestimonial.quote}
                </blockquote>
                <div className="flex items-center gap-3 mt-6">
                  <span className="font-heading font-bold text-foreground-950">{randomTestimonial.name}</span>
                  <span className="flex items-center gap-1 text-accent-500 text-xs">
                    <i className="ri-verified-badge-fill"></i>
                    <span className="text-foreground-500">{randomTestimonial.role}</span>
                  </span>
                </div>
              </div>
              <div className="md:w-2/5">
                <img
                  src={randomTestimonial.avatar}
                  alt={randomTestimonial.name}
                  className="w-full max-w-xs mx-auto rounded-2xl object-cover aspect-[3/4]"
                />
              </div>
            </div>
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row lg:items-end gap-4 mb-12 md:mb-16">
                <div className="lg:w-2/5">
                  <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 tracking-tight leading-tight">
                    同类热门
                    <br />
                    黑料
                  </h2>
                </div>
                <div className="lg:w-3/5">
                  <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                    更多{article.category}相关爆料，继续吃瓜不要停
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {relatedArticles.map((rel, index) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className={`group overflow-hidden cursor-pointer ${
                      index === 0
                        ? "relative rounded-2xl aspect-[3/4]"
                        : "bg-background-100 rounded-2xl"
                    }`}
                  >
                    {index === 0 ? (
                      <>
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-medium mb-3">
                            {rel.category}
                          </span>
                          <h3 className="font-heading text-lg font-bold text-white leading-snug line-clamp-2">
                            {rel.title}
                          </h3>
                        </div>
                      </>
                    ) : (
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                            {rel.category}
                          </span>
                        </div>
                        <div className="aspect-[16/10] rounded-xl overflow-hidden mb-4">
                          <img
                            src={rel.coverImage}
                            alt={rel.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h3>
                        <p className="text-foreground-400 text-xs">{rel.publishDate} · {rel.author}</p>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-20 md:py-32 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-12 md:mb-16 rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Dramatic%20breaking%20news%20concept%20with%20floating%20newspaper%20headlines%20and%20digital%20screens%2C%20warm%20orange%20and%20crimson%20gradient%20background%2C%20modern%20media%20aesthetic%2C%20bold%20composition%2C%20newsroom%20energy&width=1200&height=600&seq=article-cta-banner&orientation=landscape"
                alt="投稿爆料"
                className="w-full object-cover"
              />
            </div>
            <p className="font-heading text-2xl md:text-4xl lg:text-5xl text-foreground-950 leading-relaxed tracking-tight max-w-3xl mx-auto">
              你有<span className="italic text-foreground-400 font-light">猛料</span>要爆？
              <br />
              吃瓜网<span className="font-black">期待你的投稿</span>
            </p>
            <Link
              to="/about"
              className="mt-10 md:mt-14 inline-flex items-center gap-2 px-10 py-4 bg-foreground-950 text-background-50 font-medium rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              联系我们
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}